import { ItemAvatar } from 'shared/components';

import { useTLang } from 'intl/intl';
import { formatCurrency } from 'utils/CurrencyUtils';

import makeClasses from './SelectItems.style';

import type {
  ItemsReplacementModel,
  ProductReplacement,
  ToppingReplacement,
} from 'features/order/substitution-flow/domain';
import type { MouseEventHandler } from 'react';

export function SelectItems(props: Props) {
  const { isProduct, inputValue, results, onSelect } = props;

  const listToRender = resultsToRender({ isProduct, inputValue, results });
  const isEmptyInput = inputValue.trim() === '';

  const _classes = makeClasses();

  if (isEmptyInput) return null;
  return (
    <aside aria-label='select-items' className={_classes.results}>
      {listToRender.map(item => (
        <article
          key={item.id}
          data-item-id={item.id}
          data-is-selectable={item.id > 0}
          className={_classes.item}
          onClick={item.id > 0 ? onSelect : undefined}
        >
          <ItemAvatar
            variant='rounded'
            src={item.image}
            alt={item.name}
            className={_classes.image}
          />
          <div className={_classes.content}>
            <p className={_classes.name}>{item.name}</p>
            {item.price >= 0 && (
              <p className={_classes.price}>{formatCurrency(item.price)}</p>
            )}
          </div>
        </article>
      ))}
    </aside>
  );
}

interface Props {
  isProduct: boolean;
  inputValue: string;
  results: ItemsReplacementModel;
  onSelect: MouseEventHandler<HTMLElement>;
}

type ResultToRenderProps = Omit<Props, 'isFocused' | 'onSelect'>;
interface ItemToRender {
  id: number;
  name: string;
  image: string;
  price: number;
}
function resultsToRender(props: ResultToRenderProps): ItemToRender[] {
  const { isProduct, inputValue, results } = props;
  const list = isProduct ? results.products : results.toppings;
  const isEmptyList = list.length === 0;
  const hasInputValue = inputValue.trim() !== '';
  const lang = { nameEmpty: useTLang({ id: 'substitutionFlow.013' }) };
  if (isEmptyList && hasInputValue) {
    return [
      {
        id: 0,
        name: lang.nameEmpty,
        image: '',
        price: -1,
      },
    ];
  }
  return list.map<ItemToRender>(
    (item: ProductReplacement | ToppingReplacement) => ({
      id: item.id,
      name: item.name,
      image: item.image,
      price: item.price.real,
    }),
  );
}
